import { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { useAuth } from './AuthContext';

const NotificationContext = createContext(null);

const isPushSupportedCheck = () => {
  return 'Notification' in window && 'serviceWorker' in navigator;
};

const requestPushPermissionFn = async () => {
  if (!isPushSupportedCheck()) return { supported: false, permission: 'unsupported' };
  const current = Notification.permission;
  if (current === 'granted') return { supported: true, permission: 'granted' };
  if (current === 'denied') return { supported: true, permission: 'denied' };
  try {
    const permission = await Notification.requestPermission();
    return { supported: true, permission };
  } catch {
    return { supported: true, permission: 'error' };
  }
};

export const NotificationProvider = ({ children }) => {
  const { user, authFetch } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pushPermission, setPushPermission] = useState(
    isPushSupportedCheck() ? Notification.permission : 'unsupported'
  );

  // Fetch all notifications from 3 sources
  const fetchNotifications = useCallback(async () => {
    if (!user || !authFetch) return;

    setLoading(true);
    try {
      const [remindersRes, approvalsRes, notificationsRes] = await Promise.all([
        authFetch('/reminders/pending'),
        authFetch('/approvals/pending'),
        authFetch('/notifications')
      ]);

      let all = [];

      if (remindersRes.ok) {
        const data = await remindersRes.json();
        all.push(...(data.reminders || []).map(r => ({
          ...r,
          _source: 'reminder',
          type: r.reminder_type || 'reminder',
          title: r.title || 'Reminder',
          message: r.message || r.content,
          created_at: r.scheduled_for || r.created_at,
        })));
      }

      if (approvalsRes.ok) {
        const data = await approvalsRes.json();
        all.push(...(data.approvals || []).filter(a => a.status === 'pending').map(a => ({
          id: a.id,
          _source: 'approval',
          type: 'approval_required',
          title: `Approval Required: ${a.type?.replace('_', ' ') || 'Request'}`,
          message: `Requested by ${a.requested_by_name || 'team member'}`,
          status: 'unread',
          created_at: a.created_at,
          action_url: '/approvals',
          reference_id: a.reference_id,
        })));
      }

      if (notificationsRes.ok) {
        const data = await notificationsRes.json();
        all.push(...(data.notifications || []).map(n => ({
          ...n,
          _source: 'stored',
        })));
      }

      // Deduplicate by id
      const seen = new Set();
      all = all.filter(n => {
        if (seen.has(n.id)) return false;
        seen.add(n.id);
        return true;
      });

      // Sort newest first
      all.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));

      setNotifications(all);
      setUnreadCount(all.filter(n => n.status !== 'read').length);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [user, authFetch]);

  // Mark as read — route to correct endpoint based on source
  const markAsRead = async (id) => {
    const notif = notifications.find(n => n.id === id);
    try {
      if (notif?._source === 'stored') {
        await authFetch(`/notifications/${id}/read`, { method: 'POST' });
      } else {
        await authFetch(`/reminders/${id}/read`, { method: 'POST' });
      }
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, status: 'read' } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch { /* silent */ }
  };

  // Dismiss — hard delete from DB, remove from UI
  const dismiss = async (id) => {
    const notif = notifications.find(n => n.id === id);
    try {
      if (notif?._source === 'stored') {
        await authFetch(`/notifications/${id}/delete`, { method: 'DELETE' });
      } else if (notif?._source === 'approval') {
        // Approvals can't be deleted — just hide locally
      } else {
        await authFetch(`/reminders/${id}/dismiss`, { method: 'POST' });
      }
      setNotifications(prev => prev.filter(n => n.id !== id));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch { /* silent */ }
  };

  // Dismiss all — hard delete all stored notifications + dismiss all reminders
  const dismissAll = async () => {
    try {
      await Promise.all([
        authFetch('/notifications/delete-all', { method: 'DELETE' }),
        authFetch('/reminders/dismiss-all', { method: 'POST' })
      ]);
      setNotifications([]);
      setUnreadCount(0);
    } catch { /* silent */ }
  };

  // Poll every 30 seconds
  useEffect(() => {
    if (!user) return;
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [user, fetchNotifications]);

  const handleRequestPushPermission = async () => {
    const result = await requestPushPermissionFn();
    setPushPermission(result.permission);
    return result;
  };

  return (
    <NotificationContext.Provider value={{
      notifications,
      unreadCount,
      loading,
      markAsRead,
      dismiss,
      dismissAll,
      fetchNotifications,
      refresh: fetchNotifications,
      pushPermission,
      requestPushPermission: handleRequestPushPermission,
      isPushSupported: isPushSupportedCheck(),
    }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) throw new Error('useNotifications must be used within NotificationProvider');
  return context;
};
